import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { profileAvatar } from "../component/ImageList";
import baseURL from "../component/baseURL";
import { useNavigate, useLocation } from 'react-router-dom';
import stringsoflanguages from "../component/stringsoflanguages";

const MY_CONNECTION_GRID_SCROLL_KEY = 'yesmls_myConnection_gridScrollTop';
const MY_CONNECTION_RESTORE_SCROLL_PENDING_KEY = 'yesmls_myConnection_restoreScrollPending';

function readMyConnectionRestorePending() {
    try {
        return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(MY_CONNECTION_RESTORE_SCROLL_PENDING_KEY) === '1';
    } catch {
        return false;
    }
}

export default function MyConnection() {
    const navigate = useNavigate();
    const location = useLocation();
    const [userID, setUserID] = useState(null);
    const [myConnections, setMyConnections] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isPremium, setIsPremium] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [gearMenuOpen, setGearMenuOpen] = useState(false);
    const [gearSpinning, setGearSpinning] = useState(false);
    const [storedIsMember, setStoredIsMember] = useState('');
    const [storedMemberId, setStoredMemberId] = useState('');
    const [checkFilterStatus, setCheckFilterStatus] = useState(0);
    const gearMenuRef = useRef(null);
    /** `.member-cards-grid` scrolls internally (overflow-y: auto), not `window` */
    const listScrollRef = useRef(null);
    /** Hide grid until scroll is applied after back from profile — same fade pattern as GlobalMembers */
    const [gridHiddenForScrollRestore, setGridHiddenForScrollRestore] = useState(() => readMyConnectionRestorePending());

    const fromSidebar = location.state?.fromSidebar === true;
    const stateIsMember = location.state?.is_member;
    const searchParams = new URLSearchParams(location.search || '');
    const isMemberDisplay =
        !fromSidebar &&
        (stateIsMember === 1 ||
            stateIsMember === '1' ||
            searchParams.get('is_member') === '1' ||
            localStorage.getItem('is_member') === '1');
    /** After visiting a connection profile, navigate back only restores scroll — persist names like is_member */
    const memberFname =
        location.state?.member_fname ??
        searchParams.get('member_fname') ??
        localStorage.getItem('member_fname') ??
        '';
    const memberLname =
        location.state?.member_lname ??
        searchParams.get('member_lname') ??
        localStorage.getItem('member_lname') ??
        '';
    const memberDisplayName = [memberFname, memberLname].filter(Boolean).join(' ').trim() || '—';

    const searchLower = (searchQuery || '').trim().toLowerCase();
    const filteredConnections = searchLower
        ? myConnections.filter((c) => {
            const fname = (c.member_fname ?? '').toLowerCase();
            const lname = (c.member_lname ?? '').toLowerCase();
            const title = (c.title ?? '').toLowerCase();
            const city = (c.city_name ?? '').toLowerCase();
            const country = (c.country_name ?? '').toLowerCase();
            const fullName = `${fname} ${lname}`.trim();
            return fullName.includes(searchLower) || fname.includes(searchLower) || lname.includes(searchLower)
                || title.includes(searchLower) || city.includes(searchLower) || country.includes(searchLower);
        })
        : myConnections;

    const handleMemberCardClick = (member) => {
        const el = listScrollRef.current;
        const gridScroll = el ? el.scrollTop : 0;
        sessionStorage.setItem(MY_CONNECTION_GRID_SCROLL_KEY, String(gridScroll));
        sessionStorage.setItem(MY_CONNECTION_RESTORE_SCROLL_PENDING_KEY, '1');
        sessionStorage.setItem('myConnectionsScrollPosition', String(gridScroll));

        const connectionListState = { scrollPosition: gridScroll };
        if (location.state?.returnTo) {
            connectionListState.returnTo = location.state.returnTo;
        }
        if (isMemberDisplay) {
            connectionListState.is_member = 1;
            connectionListState.member_id =
                location.state?.member_id ?? storedMemberId ?? localStorage.getItem('member_id') ?? '';
            connectionListState.member_fname = memberFname;
            connectionListState.member_lname = memberLname;
        } else {
            connectionListState.fromSidebar = true;
        }

        navigate('/member-profile', {
            state: {
                memberID: member.user_id,
                member,
                scrollPosition: gridScroll,
                from: '/home/my-connection',
                returnTo: {
                    path: '/home/my-connection',
                    state: connectionListState,
                },
            },
        });
    };

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 767);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const handleGearClickOutside = (e) => {
            if (gearMenuRef.current && !gearMenuRef.current.contains(e.target)) {
                setGearMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleGearClickOutside);
        return () => document.removeEventListener('mousedown', handleGearClickOutside);
    }, []);

    useEffect(() => {
        setLoading(true);
        const userid = localStorage.getItem('user_id');
        setUserID(userid);

        if (location.state?.fromSidebar) {
            localStorage.removeItem('is_member');
            localStorage.removeItem('member_id');
            localStorage.removeItem('member_fname');
            localStorage.removeItem('member_lname');
            setStoredIsMember('');
            setStoredMemberId('');
        }

        const stateIsMember = location.state?.is_member;
        const stateMemberId = location.state?.member_id ?? '';
        const searchParams = new URLSearchParams(window.location.search);
        const fromSearchIsMember = searchParams.get('is_member');
        const fromSearchMemberId = searchParams.get('member_id') ?? '';

        if (stateIsMember !== undefined && stateIsMember !== null) {
            const isMemberVal = String(stateIsMember);
            setStoredIsMember(isMemberVal);
            localStorage.setItem('is_member', isMemberVal);
        } else if (fromSearchIsMember !== null) {
            setStoredIsMember(fromSearchIsMember);
            localStorage.setItem('is_member', fromSearchIsMember);
        } else {
            const fromStorage = localStorage.getItem('is_member');
            setStoredIsMember(fromStorage ?? '');
        }

        if (stateMemberId !== undefined && stateMemberId !== null && stateMemberId !== '') {
            const idVal = String(stateMemberId);
            setStoredMemberId(idVal);
            localStorage.setItem('member_id', idVal);
        } else if (fromSearchMemberId !== '') {
            setStoredMemberId(fromSearchMemberId);
            localStorage.setItem('member_id', fromSearchMemberId);
        } else {
            const fromStorage = localStorage.getItem('member_id');
            setStoredMemberId(fromStorage ?? '');
        }

        const stateFname = location.state?.member_fname;
        const stateLname = location.state?.member_lname;
        const searchFname = searchParams.get('member_fname');
        const searchLname = searchParams.get('member_lname');
        if (stateFname != null && String(stateFname).trim() !== '') {
            localStorage.setItem('member_fname', String(stateFname).trim());
        } else if (searchFname != null && searchFname.trim() !== '') {
            localStorage.setItem('member_fname', searchFname.trim());
        }
        if (stateLname != null && String(stateLname).trim() !== '') {
            localStorage.setItem('member_lname', String(stateLname).trim());
        } else if (searchLname != null && searchLname.trim() !== '') {
            localStorage.setItem('member_lname', searchLname.trim());
        }

        fetchMyConnectionAPI(userid);
    }, [location.state, location.search]);

    /** Persist grid scroll while browsing */
    useEffect(() => {
        if (loading) return undefined;
        const el = listScrollRef.current;
        if (!el) return undefined;
        let timeoutId;
        const onScroll = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                sessionStorage.setItem(MY_CONNECTION_GRID_SCROLL_KEY, String(el.scrollTop));
            }, 120);
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            clearTimeout(timeoutId);
            el.removeEventListener('scroll', onScroll);
        };
    }, [loading]);

    /** One-shot scroll restore before paint + fade-in (avoids triple `scrollTop` jitter). */
    useLayoutEffect(() => {
        if (loading) return undefined;
        if (sessionStorage.getItem(MY_CONNECTION_RESTORE_SCROLL_PENDING_KEY) !== '1') {
            setGridHiddenForScrollRestore(false);
            return undefined;
        }
        const raw =
            sessionStorage.getItem(MY_CONNECTION_GRID_SCROLL_KEY) ??
            sessionStorage.getItem('myConnectionsScrollPosition');
        if (raw == null || raw === '') {
            sessionStorage.removeItem(MY_CONNECTION_RESTORE_SCROLL_PENDING_KEY);
            setGridHiddenForScrollRestore(false);
            return undefined;
        }
        const y = Number(raw);
        if (!Number.isFinite(y) || y < 0) {
            sessionStorage.removeItem(MY_CONNECTION_RESTORE_SCROLL_PENDING_KEY);
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        const el = listScrollRef.current;
        if (!el) {
            sessionStorage.removeItem(MY_CONNECTION_RESTORE_SCROLL_PENDING_KEY);
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        el.scrollTop = y;
        sessionStorage.removeItem(MY_CONNECTION_RESTORE_SCROLL_PENDING_KEY);

        if (reducedMotion) {
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        requestAnimationFrame(() => {
            setGridHiddenForScrollRestore(false);
        });
        return undefined;
    }, [loading, filteredConnections.length]);

    function fetchMyConnectionAPI(userid) {
        const stateIsMember = location.state?.is_member;
        const searchParams = new URLSearchParams(window.location.search);
        const isMember = stateIsMember === 1 || stateIsMember === '1' || searchParams.get('is_member') === '1' || localStorage.getItem('is_member') === '1';
        if (isMember) {
            const memberID = (location.state?.member_id ?? searchParams.get('member_id')) || localStorage.getItem('member_id') || '';
            let formdata = new FormData();
            formdata.append("user_id", userid);
            formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055");
            formdata.append("member_type", 'my_connections');
            formdata.append("is_member", '1');
            formdata.append("member_id", memberID);
            fetch(baseURL.URL + '/member/fetch_members', {
                method: 'POST',
                body: formdata
            }).then((response) => response.json())
                .then((responseJson) => {
                    const result = responseJson.response?.member_list;
                    if (responseJson.server?.result_code === 200) {
                        const res = responseJson.response;
                        setMyConnections(result || []);
                        const premium = res.isPremium === true || res.is_premium === true || res.is_premium === 1;
                        setIsPremium(Boolean(premium));
                        setCheckFilterStatus(responseJson.response.filter_on);
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    console.error(error);
                    setLoading(false);
                });
        } else {
            let formdata = new FormData();
            formdata.append("user_id", userid);
            formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055");
            formdata.append("member_type", 'my_connections');
            fetch(baseURL.URL + '/member/fetch_members', {
                method: 'POST',
                body: formdata
            }).then((response) => response.json())
                .then((responseJson) => {
                    const result = responseJson.response?.member_list;
                    if (responseJson.server?.result_code === 200) {
                        const res = responseJson.response;
                        setMyConnections(result || []);
                        const premium = res.isPremium === true || res.is_premium === true || res.is_premium === 1;
                        setIsPremium(Boolean(premium));
                        setCheckFilterStatus(responseJson.response.filter_on);
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    console.error(error);
                    setLoading(false);
                });
        }
    }
    if (loading) {
        return (
            <div className="content-right page-loader-wrapper">
                <div className="react-js-loader">
                    <div className="react-js-loader-spinner"></div>
                    <p className="react-js-loader-text">{stringsoflanguages.loading}</p>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="content-right my-connection-page">
                {/* Search and Filter Section */}
                <div className="search-filter-section">
                    <div className="search-bar">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            className="search-icon"
                        >
                            <path
                                d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                                stroke="#E1730B"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M17.5 17.5L13.875 13.875"
                                stroke="#E1730B"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <input
                            type="text"
                            placeholder="Search by name / company"
                            className="search-input"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <button className="filter-btn">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="25"
                            height="25"
                            viewBox="0 0 25 25"
                            fill="none"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M16.4062 4.69678C14.5256 4.69678 12.9441 6.04475 12.5793 7.82178H2.34375C1.91228 7.82178 1.5625 8.17156 1.5625 8.60303C1.5625 9.0345 1.91228 9.38428 2.34375 9.38428H12.5793C12.9441 11.1613 14.5256 12.5093 16.4062 12.5093C18.2869 12.5093 19.8684 11.1613 20.2332 9.38428H22.6562C23.0877 9.38428 23.4375 9.0345 23.4375 8.60303C23.4375 8.17156 23.0877 7.82178 22.6562 7.82178H20.2332C19.8684 6.04475 18.2869 4.69678 16.4062 4.69678ZM16.4062 6.25928C17.7099 6.25928 18.75 7.29938 18.75 8.60303C18.75 9.90668 17.7099 10.9468 16.4062 10.9468C15.1026 10.9468 14.0625 9.90668 14.0625 8.60303C14.0625 7.29938 15.1026 6.25928 16.4062 6.25928Z"
                                fill="white"
                            />
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M8.59375 12.5093C6.71313 12.5093 5.1316 13.8573 4.76685 15.6343H2.34375C1.91228 15.6343 1.5625 15.9841 1.5625 16.4155C1.5625 16.847 1.91228 17.1968 2.34375 17.1968H4.76685C5.1316 18.9738 6.71313 20.3218 8.59375 20.3218C10.4744 20.3218 12.0559 18.9738 12.4207 17.1968H22.6562C23.0877 17.1968 23.4375 16.847 23.4375 16.4155C23.4375 15.9841 23.0877 15.6343 22.6562 15.6343H12.4207C12.0559 13.8573 10.4744 12.5093 8.59375 12.5093ZM8.59375 14.0718C9.8974 14.0718 10.9375 15.1119 10.9375 16.4155C10.9375 17.7192 9.8974 18.7593 8.59375 18.7593C7.2901 18.7593 6.25 17.7192 6.25 16.4155C6.25 15.1119 7.2901 14.0718 8.59375 14.0718Z"
                                fill="white"
                            />
                        </svg>
                    </button>
                </div>

                {isMemberDisplay && (
                    <div className="agent-name-sec">
                        {memberDisplayName}'s {stringsoflanguages.connections}
                    </div>
                )}

                <span className='listing-count'>{stringsoflanguages.noofmembers}: {filteredConnections.length}</span>
                {/* Member Cards Grid */}
                <div
                    className={`member-cards-grid${gridHiddenForScrollRestore ? ' gm-grid--restore-hidden' : ''}`}
                    ref={listScrollRef}
                >
                    {filteredConnections.length === 0 ? (
                        <p className="no-data-message">{searchQuery.trim() ? 'No connections match your search.' : 'No connections yet.'}</p>
                    ) : (
                        filteredConnections.map((connection) => (
                            <div className="member-card cursor-pointer" key={connection.id} onClick={() => handleMemberCardClick(connection)}>
                                <div className="member-info">
                                    <p className="member-since">{stringsoflanguages.memberSince} {connection.added_date}</p>
                                    <h3 className="member-name">
                                        {connection.member_fname} {connection.member_lname}
                                        {(connection.isPremium === true || connection.is_premium === true || connection.is_premium === 1 || isPremium) ? (
                                            <span className="premiumtype">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
                                                    <path d="M7.2868 9.91064L10.7796 22.0513L0 9.91064H7.2868Z" fill="#E1730B" />
                                                    <path d="M7.42024 8.38808H0L6.86669 2.01175C7.00926 1.87014 7.20204 1.79102 7.403 1.79102H11.2271L7.42024 8.38808Z" fill="#E1730B" />
                                                    <path d="M9.1778 8.38811L12.9841 1.7915L16.7905 8.38811H9.1778Z" fill="#E1730B" />
                                                    <path d="M17.098 9.91064L12.9842 24.2091L8.87036 9.91064H17.098Z" fill="#E1730B" />
                                                    <path d="M18.5481 8.38813L14.7412 1.79102H19.0727C19.2843 1.79102 19.4857 1.87881 19.6298 2.03361L26 8.38813H18.5481Z" fill="#E1730B" />
                                                    <path d="M25.9683 9.91064L15.1887 22.0513L18.6815 9.91064H25.9683Z" fill="#E1730B" />
                                                </svg>
                                            </span>
                                        ) : null}
                                    </h3>
                                    <p className="member-role">{connection.title}</p>
                                    <p className="member-location">{connection.city_name}, {connection.country_name}</p>
                                </div>
                                <div className="member-image">
                                    <img src={connection.member_image_thumb} alt="Biswajit Das" />
                                </div>
                            </div>
                        ))
                    )}


                </div>

                {/* Desktop only: gear icon with dropdown (Filter / Sliders) */}
                {!isMobile && (
                    <div className="btn-section btn-section-gear" ref={gearMenuRef}>
                        <button
                            type="button"
                            className={`btn-primary gear-btn ${gearSpinning ? 'gear-spin' : ''}`}
                            onClick={() => {
                                setGearMenuOpen((prev) => !prev);
                                setGearSpinning(true);
                                setTimeout(() => setGearSpinning(false), 500);
                            }}
                            aria-label="Options"
                            aria-expanded={gearMenuOpen}
                        >
                            <i className="fa-solid fa-gear"></i>
                        </button>
                        {gearMenuOpen && (
                            <ul className="gear-dropdown" aria-label="Options list">

                                <li>
                                    <button
                                        type="button"
                                        className="gear-dropdown-item"
                                        onClick={() => {
                                            setGearMenuOpen(false);
                                            navigate('/home/member-filter-my-connection');
                                        }}
                                    >
                                        {checkFilterStatus === 1 ?
                                            <span className="filter-active-icon"><i className="fa-solid fa-check"></i></span> : null}
                                        <i className="fa-solid fa-sliders"></i>
                                        <span>{stringsoflanguages.filter}</span>
                                    </button>
                                </li>
                            </ul>
                        )}
                    </div>
                )}
            </div>

            {/* Mobile: bottom nav with sliders icon */}
            {isMobile && (
                <nav className="mobile-bottom-nav liquidGlass-wrapper dock my-connection-footer-nav" aria-label="Connection actions">
                    <div className="liquidGlass-effect" aria-hidden="true" />
                    <div className="liquidGlass-shine" aria-hidden="true" />
                    <div className="mobile-nav-item" aria-label="Home" onClick={() => navigate('/home')}>
                        <i className="fa-solid fa-house"></i>
                    </div>
                    <div
                        className="mobile-nav-item cursor-pointer"
                        role="button"
                        tabIndex={0}
                        aria-label={stringsoflanguages.filter}
                        onClick={() => navigate('/home/member-filter-my-connection')}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate('/home/member-filter-my-connection');
                            }
                        }}
                    >{checkFilterStatus === 1 ?
                        <span className="filter-active-icon"><i className="fa-solid fa-check"></i></span> : null}
                        <i className="fa-solid fa-sliders"></i>
                    </div>
                </nav>
            )}
            <svg style={{ display: 'none' }} aria-hidden="true">
                <filter id="glass-distortion-my-connection" x="0%" y="0%" width="100%" height="100%" filterUnits="objectBoundingBox">
                    <feTurbulence type="fractalNoise" baseFrequency="0.01 0.01" numOctaves="1" seed="5" result="turbulence" />
                    <feComponentTransfer in="turbulence" result="mapped">
                        <feFuncR type="gamma" amplitude="1" exponent="10" offset="0.5" />
                        <feFuncG type="gamma" amplitude="0" exponent="1" offset="0" />
                        <feFuncB type="gamma" amplitude="0" exponent="1" offset="0.5" />
                    </feComponentTransfer>
                    <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />
                    <feSpecularLighting in="softMap" surfaceScale="5" specularConstant="1" specularExponent="100" lightingColor="white" result="specLight">
                        <fePointLight x="-200" y="-200" z="300" />
                    </feSpecularLighting>
                    <feComposite in="specLight" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="litImage" />
                    <feDisplacementMap in="SourceGraphic" in2="softMap" scale="150" xChannelSelector="R" yChannelSelector="G" />
                </filter>
            </svg>
        </React.Fragment>
    );
}